/**
 * 입시 인사이트 페이지 — 5년 누적 변경사항 분석.
 *
 * - 의대/약대 트렌드 (학년도별/대학별)
 * - AI/바이오 신설 학과 리스트
 * - 변경사항 카테고리별 검색
 * - 수능 최저/평가방법 변경 알림
 */
import React, { useEffect, useState } from 'react';
import hqAdmissionService, {
  ChangeItem,
  ChangesResponse,
  InsightsResponse,
} from '../services/hqAdmissionService';

const HqAdmissionInsightsPage: React.FC = () => {
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [aiBio, setAiBio] = useState<{ year: number; univ: string; content: string }[]>([]);
  const [medicalTrend, setMedicalTrend] = useState<{ by_year: Record<string, number>; by_univ_top10: Record<string, number>; total: number } | null>(null);
  const [tab, setTab] = useState<'overview' | 'medical' | 'aibio' | 'changes' | 'method'>('overview');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 변경사항 검색 상태
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchYear, setSearchYear] = useState<string>('');
  const [searchCategory, setSearchCategory] = useState<string>('');
  const [searchResults, setSearchResults] = useState<ChangesResponse | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [ins, bio, mt] = await Promise.all([
          hqAdmissionService.insights(),
          hqAdmissionService.aiBioNew(),
          hqAdmissionService.medicalTrend(),
        ]);
        setInsights(ins);
        setAiBio(bio.new_depts || []);
        setMedicalTrend(mt);
      } catch (e: any) {
        setError(e.response?.data?.detail || '데이터 로딩 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const doSearch = async () => {
    setSearching(true);
    try {
      const r = await hqAdmissionService.searchChanges({
        keyword: searchKeyword || undefined,
        year: searchYear ? parseInt(searchYear) : undefined,
        category: searchCategory || undefined,
        limit: 100,
      });
      setSearchResults(r);
    } catch (e: any) {
      setError(e.response?.data?.detail || '검색 실패');
    } finally {
      setSearching(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">로딩 중...</div>;
  if (error) return <div className="p-8 text-red-600">⚠️ {error}</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">📊 5년 누적 입시 인사이트</h1>
      <p className="text-sm text-slate-600 mb-6">
        adiga.kr 2021~2025학년도 결과 공통탭 자동 분석 (총 {insights?.summary.total.toLocaleString()}건)
      </p>

      {/* 탭 */}
      <div className="flex gap-2 mb-6 border-b">
        {[
          { k: 'overview', label: '📊 종합' },
          { k: 'medical', label: '🏥 의대/약대' },
          { k: 'aibio', label: '🤖 AI/바이오' },
          { k: 'method', label: '📝 평가방법' },
          { k: 'changes', label: '🔍 변경사항 검색' },
        ].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => setTab(k as any)}
            className={`px-4 py-2 ${
              tab === k
                ? 'border-b-2 border-blue-600 text-blue-600 font-semibold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 종합 */}
      {tab === 'overview' && insights && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="전체 변경사항" value={insights.summary.total} color="blue" />
            <StatCard label="의대/약대 영향" value={insights.highlights.medical.length} color="red" />
            <StatCard label="AI/바이오 신설" value={insights.highlights.ai_bio.length} color="purple" />
            <StatCard label="평가방법 변경" value={insights.highlights.admission_method.length} color="green" />
          </div>

          <div>
            <h3 className="font-semibold mb-2">학년도별 변경사항</h3>
            <div className="grid grid-cols-5 gap-2">
              {Object.entries(insights.summary.by_year)
                .sort()
                .map(([y, n]) => (
                  <div key={y} className="bg-white p-3 rounded shadow-sm text-center">
                    <div className="text-xs text-slate-500">{y}학년도</div>
                    <div className="text-2xl font-bold">{n}</div>
                  </div>
                ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">카테고리 분포 (Top 10)</h3>
            <div className="bg-white rounded shadow-sm overflow-hidden">
              {Object.entries(insights.summary.by_category)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([c, n]) => {
                  const max = Math.max(...Object.values(insights.summary.by_category));
                  const pct = (n / max) * 100;
                  return (
                    <div key={c} className="flex items-center px-4 py-2 border-b">
                      <div className="w-40 text-sm">{c}</div>
                      <div className="flex-1 bg-slate-100 h-4 rounded relative">
                        <div
                          className="bg-blue-500 h-full rounded"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="w-16 text-right text-sm font-semibold">
                        {n.toLocaleString()}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* 의대 */}
      {tab === 'medical' && medicalTrend && insights && (
        <div className="space-y-6">
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <div className="text-lg font-bold text-red-700">
              🏥 5년 누적 의대/약대 변경 {medicalTrend.total}건
            </div>
            <div className="text-sm mt-1 text-red-600">
              2025학년도부터 의대 정원 1,500 → 4,500명 증원 영향
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2">학년도별 의대 변경</h3>
              <div className="bg-white rounded shadow-sm p-4">
                {Object.entries(medicalTrend.by_year).map(([y, n]) => (
                  <div key={y} className="flex items-center mb-2">
                    <div className="w-20 text-sm">{y}</div>
                    <div className="flex-1 bg-slate-100 h-6 rounded relative">
                      <div
                        className="bg-red-400 h-full rounded"
                        style={{
                          width: `${(n / Math.max(...Object.values(medicalTrend.by_year))) * 100}%`,
                        }}
                      />
                    </div>
                    <div className="w-12 text-right font-semibold">{n}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">대학별 변경 Top 10</h3>
              <div className="bg-white rounded shadow-sm p-4 text-sm">
                {Object.entries(medicalTrend.by_univ_top10).map(([u, n]) => (
                  <div key={u} className="flex justify-between py-1">
                    <span>{u}</span>
                    <span className="font-semibold">{n}건</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">최근 의대 변경 사항 (Top 20)</h3>
            <ChangeList items={insights.highlights.medical} />
          </div>
        </div>
      )}

      {/* AI/바이오 */}
      {tab === 'aibio' && (
        <div>
          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-4">
            <div className="text-lg font-bold text-purple-700">
              🤖 AI/바이오 신설 학과 {aiBio.length}개
            </div>
            <div className="text-sm mt-1 text-purple-600">
              인공지능·반도체·바이오로직스·데이터사이언스 등 첨단분야
            </div>
          </div>

          <div className="space-y-2">
            {aiBio.map((m, i) => (
              <div key={i} className="bg-white rounded shadow-sm p-4">
                <div className="flex justify-between items-start mb-1">
                  <div className="font-semibold">{m.univ}</div>
                  <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                    {m.year}
                  </span>
                </div>
                <div className="text-sm text-slate-700">{m.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 평가방법 */}
      {tab === 'method' && insights && (
        <div>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <div className="text-lg font-bold text-green-700">
              📝 평가방법 변경 {insights.highlights.admission_method.length}건
            </div>
            <div className="text-sm mt-1 text-green-600">
              수능 최저학력기준, 학생부 반영방법, 진로선택 반영, 영역별 비율 등
            </div>
          </div>
          <ChangeList items={insights.highlights.admission_method} />
        </div>
      )}

      {/* 변경사항 검색 */}
      {tab === 'changes' && (
        <div>
          <div className="bg-white p-4 rounded shadow-sm mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                type="text"
                placeholder="키워드 (예: 의예과)"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="px-3 py-2 border rounded col-span-2"
              />
              <select
                value={searchYear}
                onChange={(e) => setSearchYear(e.target.value)}
                className="px-3 py-2 border rounded"
              >
                <option value="">학년도 전체</option>
                {[2021, 2022, 2023, 2024, 2025].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                value={searchCategory}
                onChange={(e) => setSearchCategory(e.target.value)}
                className="px-3 py-2 border rounded"
              >
                <option value="">카테고리 전체</option>
                {[
                  '학과_신설',
                  '학과_폐지',
                  '학과_통합',
                  '정원_증원',
                  '정원_감원',
                  '수능최저_변경',
                  '학생부반영_변경',
                  '진로선택_변경',
                  '권장과목_변경',
                  '면접_변경',
                  '논술_변경',
                  '서류평가_변경',
                  '첨단학과_신설',
                ].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={doSearch}
              disabled={searching}
              className="mt-3 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {searching ? '검색 중...' : '🔍 검색'}
            </button>
          </div>

          {searchResults && (
            <>
              <div className="text-sm text-slate-600 mb-2">
                {searchResults.total.toLocaleString()}건 발견 (상위 100건 표시)
              </div>
              <ChangeList items={searchResults.results} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; color: 'blue' | 'red' | 'purple' | 'green' }> = ({ label, value, color }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700',
    red: 'bg-red-50 text-red-700',
    purple: 'bg-purple-50 text-purple-700',
    green: 'bg-green-50 text-green-700',
  };
  return (
    <div className={`${colorMap[color]} p-4 rounded-lg`}>
      <div className="text-sm">{label}</div>
      <div className="text-3xl font-bold mt-1">{value.toLocaleString()}</div>
    </div>
  );
};

const ChangeList: React.FC<{ items: ChangeItem[] }> = ({ items }) => {
  if (!items.length) return <div className="text-slate-500 text-center py-8">결과가 없습니다.</div>;
  return (
    <div className="space-y-2">
      {items.map((c, i) => (
        <div key={i} className="bg-white rounded shadow-sm p-4">
          <div className="flex justify-between items-start mb-1">
            <div className="flex items-center gap-2">
              <div className="font-semibold">{c.univ}</div>
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  c.severity === 'high'
                    ? 'bg-red-100 text-red-700'
                    : c.severity === 'medium'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-slate-100 text-slate-600'
                }`}
              >
                {c.category || c.sub_category}
              </span>
            </div>
            <span className="text-xs text-slate-500">{c.result_year}학년도</span>
          </div>
          <div className="text-sm text-slate-700 leading-relaxed">{c.content}</div>
        </div>
      ))}
    </div>
  );
};

export default HqAdmissionInsightsPage;
